import React, { useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { TbMessageCircle } from 'react-icons/tb';
import useReportComments from '../../../../../hooks/useReportComments';
import * as Styled from './style';
import { COMMENT_REPORT_TYPES, COMMENT_CUSTOM_FUND_NAMES } from '../constants';
import type { SchemaCommentThreadResponseSchema } from '../../../../../api/generated/schema';

interface ReportSectionCommentCounterProps {
  fundId?: string | null;
  tableName?: string | null;
  isMultiFunds?: boolean;
  collapsed?: boolean;
}

const ReportSectionCommentCounter = ({
  fundId,
  tableName,
  isMultiFunds = false,
  collapsed = false
}: ReportSectionCommentCounterProps) => {
  const { workflowId, periodId } = useParams();
  const location = useLocation();
  const isFCAReport = location?.pathname?.startsWith('/report/fca');
  const [searchParams] = useSearchParams();
  const currentReportType = isFCAReport ? COMMENT_REPORT_TYPES.FCA : COMMENT_REPORT_TYPES.ESMA;
  const isAIFM = searchParams.get('type') === 'AIF001';

  const formattedFundName = useMemo(() => {
    let customFundName: string | null = null;

    if (isAIFM && !fundId) {
      customFundName = isFCAReport
        ? COMMENT_CUSTOM_FUND_NAMES.FCA_FUND_NAME
        : COMMENT_CUSTOM_FUND_NAMES.ESMA_FUND_NAME;
    }
    return customFundName;
  }, [searchParams, isFCAReport, fundId]);

  const { data = [] } = useReportComments({
    workflowId,
    periodId,
    fundId: fundId,
    fundName: formattedFundName,
    tableName,
    reportType: currentReportType
  }) as unknown as { data?: SchemaCommentThreadResponseSchema[] };

  const filteredTableCommentsData = useMemo(() => {
    return data.filter(thread => {
      if (thread.is_resolved) {
        return false;
      }
      let matchesFund = !isAIFM
        ? thread.fund_id === fundId
        : thread.fund_name === formattedFundName;
      if (isMultiFunds) {
        matchesFund = true;
      }
      const matchesTableName = thread.table_name === tableName;
      const matchesReportType = thread.report_type === currentReportType;
      return matchesFund && matchesTableName && matchesReportType;
    });
  }, [data, workflowId, fundId, formattedFundName, tableName, currentReportType, isMultiFunds]);

  const totalTableSectionComments = useMemo(() => {
    return filteredTableCommentsData.reduce(
      (accumulator, currentThread) => accumulator + currentThread?.messages?.length || 0,
      0
    );
  }, [filteredTableCommentsData]);

  if (totalTableSectionComments === 0) {
    return null;
  }

  return (
    <Styled.CommentCounterContainer
      id={`report-section-comment-counter-${tableName ?? 'unknown'}`}
      $collapsed={collapsed}
    >
      <span className="comment-count" id={`report-section-comment-count-${tableName ?? 'unknown'}`}>
        {totalTableSectionComments}
      </span>
      <TbMessageCircle color="#ADB5BD" />
    </Styled.CommentCounterContainer>
  );
};

export default ReportSectionCommentCounter;
